import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { NOTIFICATION_TYPE } from '../../constants/common'
import { useNotification } from '../../hooks'
import { getQaSelector } from '../../utils/common'
import { NotificationInline } from '../Notification'

import { QA_NAME_CHATROOM } from './Chatroom.constants'
import { useChatroomInit, useChatroomMessages } from './Chatroom.hooks'
import {
  getChatroomEditDraft,
  getChatroomSendPermissions,
  getIsChatroomVisibilityBlocked,
  saveChatroomEditDraft,
} from './Chatroom.utils'
import { ChatroomHeader } from './ChatroomHeader'
import {
  ChatroomInputForm,
  ChatroomInputFormSkeleton,
} from './ChatroomInputForm'
import { ChatroomMessages } from './ChatroomMessages'

import * as styles from './Chatroom.module.css'

function Chatroom({
  EmptyState,
  Header = ChatroomHeader,
  buildingId,
  chatroomData,
  className = styles.Chatroom,
  compact,
  createChatroomParams,
  disableSend,
  filePreviewProps,
  isFullScreen,
  isPrivate,
  isPublic,
  messageDraftRef,
  outboundUuid,
  qaName = QA_NAME_CHATROOM,
  requestId,
  requestPermissions,
  title,
  uuid,
  visibleToDwellers,
  onChatroomCreated,
  onLoadedChange,
  onMessageDeleted,
  onMessageRequestsChanged,
  onMessagesAdded,
  onMessageSent,
  onMessageUpdated,
  onReadBottomReached,
  onToggleFullScreen,
}) {
  const { t } = useTranslation()

  const { handleAddErrorNotification } = useNotification()

  // Restores the in-place edit form after a full screen toggle remount — the
  // unsaved edit itself lives in `messageDraftRef` next to the compose draft.
  const [editingMessageUuid, setEditingMessageUuid] = useState(
    () => getChatroomEditDraft(messageDraftRef)?.key || null
  )

  const { chatroom, handleMarkAsRead } = useChatroomInit({
    chatroomData,
    outboundUuid,
    uuid,
  })

  function handleFinishEditMessage() {
    setEditingMessageUuid(null)
    saveChatroomEditDraft(messageDraftRef, null)
  }

  function handleMessageDeletedRemotely(deletedUuid) {
    if (deletedUuid === editingMessageUuid) {
      handleAddErrorNotification('Chatroom.MessageEditDeletedErrorKey')
      handleFinishEditMessage()
    }
  }

  const {
    actions,
    hasMoreOlder,
    isInitialReady,
    isLoadingOlder,
    messages,
    unreadAnchorMessageUuid,
  } = useChatroomMessages({
    chatroom,
    outboundUuid,
    uuid,
    onMarkAsRead: handleMarkAsRead,
    onMessageDeletedRemotely: handleMessageDeletedRemotely,
    onMessagesAdded,
    onMessageUpdatedRemotely: onMessageUpdated,
    onReadBottomReached,
  })

  // Reports the messages readiness to the parent; the cleanup resets it on unmount.
  useEffect(() => {
    onLoadedChange?.(isInitialReady)

    return () => {
      onLoadedChange?.(false)
    }
  }, [isInitialReady])

  const qaProps = getQaSelector({ context: QA_NAME_CHATROOM, value: qaName })

  const chatroomClassName = classNames(className, {
    [styles.chatroomFullScreen]: isFullScreen,
  })

  const contentAreaClassName = classNames(styles.contentArea, {
    [styles.contentAreaCompact]: compact,
    [styles.contentAreaFullScreen]: isFullScreen,
  })

  const isVisibilityBlocked = getIsChatroomVisibilityBlocked({
    isPublic,
    visibleToDwellers,
  })

  const sendPermissions = getChatroomSendPermissions({
    chatroom,
    disableSend,
    requestPermissions,
    uuid,
  })

  function handleSendSuccess(data) {
    actions.append(data)
    onMessagesAdded?.([data])
    onMessageSent?.()
  }

  function handleRefetchVisibleMessages() {
    actions.refetchVisible()
    onMessageRequestsChanged?.()
  }

  function handleUpdateMessage(messageUuid, updates) {
    actions.update(messageUuid, updates)

    if (updates.deleted) {
      onMessageDeleted?.()
    }
  }

  function handleMessageSaved(data) {
    actions.replace(data)
    onMessageUpdated?.(data)
  }

  return (
    <div {...qaProps} className={chatroomClassName}>
      <div className={contentAreaClassName}>
        {Header ? (
          <Header
            isFullScreen={isFullScreen}
            title={title}
            onToggleFullScreen={onToggleFullScreen}
          />
        ) : null}
        {isVisibilityBlocked ? (
          <div className={styles.notificationWrap}>
            <NotificationInline
              text={t('Chatroom.PublicChatVisibilityBlockedKey')}
              type={NOTIFICATION_TYPE.warning}
            />
          </div>
        ) : null}
        <ChatroomMessages
          EmptyState={EmptyState}
          chatroomCreated={chatroom?.created}
          editingMessageUuid={editingMessageUuid}
          filePreviewProps={filePreviewProps}
          hasMoreOlder={hasMoreOlder}
          isFullScreen={isFullScreen}
          isInitialReady={isInitialReady}
          isLoadingOlder={isLoadingOlder}
          isPrivate={isPrivate}
          messageDraftRef={messageDraftRef}
          messages={messages}
          outboundUuid={outboundUuid}
          unreadAnchorMessageUuid={unreadAnchorMessageUuid}
          onEditMessage={setEditingMessageUuid}
          onFinishEditMessage={handleFinishEditMessage}
          onLoadOlderPage={actions.loadOlder}
          onMessageSaved={handleMessageSaved}
          onReadBottomReached={actions.readBottomReached}
          onRefetchVisibleMessages={handleRefetchVisibleMessages}
          onUpdateMessage={handleUpdateMessage}
        />
      </div>
      {isVisibilityBlocked ? null : !isInitialReady ? (
        <ChatroomInputFormSkeleton />
      ) : sendPermissions?.canSendMessage ? (
        <ChatroomInputForm
          buildingId={buildingId}
          canSendAiGeneratedMessage={sendPermissions?.canSendAiGeneratedMessage}
          canSendTemplatedMessage={sendPermissions?.canSendTemplatedMessage}
          chatroomUuid={uuid}
          createChatroomParams={createChatroomParams}
          isFullScreen={isFullScreen}
          isPrivate={isPrivate}
          isPublic={isPublic}
          messageDraftRef={messageDraftRef}
          outboundUuid={outboundUuid}
          requestId={requestId}
          onChatroomCreated={onChatroomCreated}
          onSendSuccess={handleSendSuccess}
        />
      ) : null}
    </div>
  )
}

export default Chatroom
